import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  apiAddDepartments,
  apiAddDesignations,
  apiAddEmploymentStatuses,
  apiAddHolidays,
  apiDeleteDepartments,
  apiDeleteDesignations,
  apiDeleteEmploymentStatuses,
  apiDeleteHolidays,
  getAllTodayAttendance,
  getAttendanceMetadata,
  getCurrentUserMonthlyReport,
  getMonthlyReport,
  getUserRouteData,
} from "../../api/attendance.api";
import { IUser } from "../../models/userData.model";
import {
  AdminAttendanceRecord,
  AttendanceRecord,
} from "../../models/attendance.model";

interface Holiday {
  _id: string;
  date: string;
  name: string;
}

interface AttendanceState {
  loading: boolean;
  error: string | null;
  todayRecord: AttendanceRecord | null;
  monthlyRecords: AttendanceRecord[];
  allTodayRecords: AdminAttendanceRecord[];
  currentUserMonthlyRecords: AttendanceRecord[];
  routeData: { lat: number; lng: number; timestamp?: string }[];
  attendanceMetadata: {
    employmentStatus: string[];
    attendance: string[];
    weekendPolicy: string[];
    departments: string[];
    paymentTypes: string[];
    banks: string[];
    gender: string[];
    bloodGroup: string[];
    designations: string[];
    locations: string[];
    attendanceMethods: string[];
    weekendPolicies: string[];
    employmentStatuses: string[];
    maritalStatus: string[];
    resignationModes: string[];
  } | null;
  holidays: Holiday[];
}

const initialState: AttendanceState = {
  loading: false,
  error: null,
  todayRecord: null,
  monthlyRecords: [],
  allTodayRecords: [],
  routeData: [],
  currentUserMonthlyRecords: [],
  attendanceMetadata: null,
  holidays: [],
};

export const fetchUserRoute = createAsyncThunk(
  "attendance/fetchUserRoute",
  async (payload: { userId: string; date: string }, { rejectWithValue }) => {
    const { userId, date } = payload;

    try {
      const data = await getUserRouteData(userId, date);

      const formatted = data.map(
        (point: { location: { lat: any; lng: any }; timestamp: any }) => ({
          latitude: point.location.lat,
          longitude: point.location.lng,
          timestamp: point.timestamp,
        })
      );

      return formatted;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error fetching route");
    }
  }
);

export const fetchMonthlyReport = createAsyncThunk<
  AttendanceRecord[],
  { month: number; year: number; userId?: string },
  { rejectValue: string }
>(
  "attendance/fetchMonthlyReport",
  async ({ month, year, userId }, { rejectWithValue }) => {
    try {
      const resp = await getMonthlyReport(month, year, userId);
      return resp.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchAllTodayAttendance = createAsyncThunk<
  AdminAttendanceRecord[],
  { search?: string; date?: string },
  { rejectValue: string }
>(
  "attendance/fetchAllTodayAttendance",
  async ({ search, date }, { rejectWithValue }) => {
    try {
      const response = await getAllTodayAttendance(search, date);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchAttendanceMetadata = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>("attendance/fetchAttendanceMetadata", async (_, { rejectWithValue }) => {
  try {
    const resp = await getAttendanceMetadata();
    return resp.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message);
  }
});

export const fetchCurrentUserMonthlyReport = createAsyncThunk<
  AttendanceRecord[],
  { month: number; year: number },
  { rejectValue: string }
>(
  "attendance/fetchCurrentUserMonthlyReport",
  async ({ month, year }, { rejectWithValue }) => {
    try {
      const resp = await getCurrentUserMonthlyReport(month, year);
      return resp.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const addHolidays = createAsyncThunk<
  Holiday[],
  { date: string; name: string }[],
  { rejectValue: string }
>("attendance/addHolidays", async (holidays, { rejectWithValue }) => {
  try {
    return await apiAddHolidays(holidays);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to add holidays");
  }
});

export const addDepartments = createAsyncThunk<
  string[],
  string[],
  { rejectValue: string }
>("attendance/addDepartments", async (names, { rejectWithValue }) => {
  try {
    return await apiAddDepartments(names);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to add departments");
  }
});

export const addDesignations = createAsyncThunk<
  string[],
  string[],
  { rejectValue: string }
>("attendance/addDesignations", async (names, { rejectWithValue }) => {
  try {
    return await apiAddDesignations(names);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to add designations");
  }
});

export const addEmploymentStatuses = createAsyncThunk<
  string[],
  string[],
  { rejectValue: string }
>("attendance/addEmploymentStatuses", async (names, { rejectWithValue }) => {
  try {
    return await apiAddEmploymentStatuses(names);
  } catch (error: any) {
    return rejectWithValue(
      error.message || "Failed to add employment statuses"
    );
  }
});

export const deleteHolidays = createAsyncThunk<Holiday[], string[]>(
  "attendance/deleteHolidays",
  async (dates, { rejectWithValue }) => {
    try {
      return await apiDeleteHolidays(dates);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const deleteDepartments = createAsyncThunk<string[], string[]>(
  "attendance/deleteDepartments",
  async (names, { rejectWithValue }) => {
    try {
      return await apiDeleteDepartments(names);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const deleteDesignations = createAsyncThunk<string[], string[]>(
  "attendance/deleteDesignations",
  async (names, { rejectWithValue }) => {
    try {
      return await apiDeleteDesignations(names);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

export const deleteEmploymentStatuses = createAsyncThunk<string[], string[]>(
  "attendance/deleteEmploymentStatuses",
  async (names, { rejectWithValue }) => {
    try {
      return await apiDeleteEmploymentStatuses(names);
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

const attendanceSlice = createSlice({
  name: "attendance",
  initialState,
  reducers: {
    clearAttendanceError: (state) => {
      state.error = null;
    },
    updateAttendanceRecord: (
      state,
      action: PayloadAction<{
        date: string;
        session1Status: "Present" | "Absent";
        session2Status: "Present" | "Absent";
      }>
    ) => {
      const { date, session1Status, session2Status } = action.payload;

      const index = state.currentUserMonthlyRecords.findIndex(
        (r) => r.date === date
      );

      if (index !== -1) {
        state.currentUserMonthlyRecords[index] = {
          ...state.currentUserMonthlyRecords[index],
          firstSessionStatus: session1Status,
          secondSessionStatus: session2Status,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchUserRoute.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserRoute.fulfilled, (state, action) => {
        state.loading = false;
        state.routeData = action.payload;
      })
      .addCase(fetchUserRoute.rejected, (state, action) => {
        state.loading = false;
        state.routeData = [];
      })
      .addCase(fetchMonthlyReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchMonthlyReport.fulfilled,
        (state, action: PayloadAction<AttendanceRecord[]>) => {
          state.loading = false;
          state.monthlyRecords = action.payload;
        }
      )
      .addCase(fetchMonthlyReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch All Today's Attendance cases
      .addCase(fetchAllTodayAttendance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllTodayAttendance.fulfilled,
        (state, action: PayloadAction<AdminAttendanceRecord[]>) => {
          state.loading = false;
          state.allTodayRecords = action.payload;
        }
      )
      .addCase(fetchAllTodayAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAttendanceMetadata.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAttendanceMetadata.fulfilled, (state, action) => {
        state.loading = false;
        state.attendanceMetadata = action.payload;
        state.holidays = action.payload?.holidays ?? [];
      })
      .addCase(fetchAttendanceMetadata.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch metadata";
      })
      .addCase(fetchCurrentUserMonthlyReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCurrentUserMonthlyReport.fulfilled,
        (state, action: PayloadAction<AttendanceRecord[]>) => {
          state.loading = false;
          state.currentUserMonthlyRecords = action.payload;
        }
      )
      .addCase(fetchCurrentUserMonthlyReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // === ADD METADATA CASES ===
      .addCase(addHolidays.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addHolidays.fulfilled, (state, action) => {
        state.loading = false;
        state.holidays = action.payload;
      })
      .addCase(addHolidays.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(addDepartments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addDepartments.fulfilled, (state, action) => {
        state.loading = false;
        if (state.attendanceMetadata) {
          state.attendanceMetadata.departments = action.payload;
        }
      })
      .addCase(addDepartments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(addDesignations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addDesignations.fulfilled, (state, action) => {
        state.loading = false;
        if (state.attendanceMetadata) {
          state.attendanceMetadata.designations = action.payload;
        }
      })
      .addCase(addDesignations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(addEmploymentStatuses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addEmploymentStatuses.fulfilled, (state, action) => {
        state.loading = false;
        if (state.attendanceMetadata) {
          state.attendanceMetadata.employmentStatuses = action.payload;
        }
      })
      .addCase(addEmploymentStatuses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteHolidays.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(deleteHolidays.fulfilled, (s, a) => {
        s.loading = false;
        s.holidays = a.payload;
      })
      .addCase(deleteHolidays.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload as string;
      })

      .addCase(deleteDepartments.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(deleteDepartments.fulfilled, (s, a) => {
        s.loading = false;
        if (s.attendanceMetadata) s.attendanceMetadata.departments = a.payload;
      })
      .addCase(deleteDepartments.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload as string;
      })

      .addCase(deleteDesignations.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(deleteDesignations.fulfilled, (s, a) => {
        s.loading = false;
        if (s.attendanceMetadata) s.attendanceMetadata.designations = a.payload;
      })
      .addCase(deleteDesignations.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload as string;
      })

      .addCase(deleteEmploymentStatuses.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(deleteEmploymentStatuses.fulfilled, (s, a) => {
        s.loading = false;
        if (s.attendanceMetadata)
          s.attendanceMetadata.employmentStatuses = a.payload;
      })
      .addCase(deleteEmploymentStatuses.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload as string;
      });
  },
});

export const { clearAttendanceError, updateAttendanceRecord } =
  attendanceSlice.actions;
export default attendanceSlice.reducer;
